import React, { useCallback, useEffect, useRef, useState } from 'react';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm, useWatch } from 'react-hook-form';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import CSSZSickLeaveCertificateFormPartE from './FormParts/CSSZSickLeaveCertificateFormPartE';
import CSSZSickLeaveCertificateFormPartD from './FormParts/CSSZSickLeaveCertificateFormPartD';
import CSSZSickLeaveCertificateFormPartC, {
	type CSSZSickLeaveCertificateFormPartCProps,
} from './FormParts/CSSZSickLeaveCertificateFormPartC';
import CSSZSickLeaveCertificateFormPartB from './FormParts/CSSZSickLeaveCertificateFormPartB';
import CSSZSickLeaveCertificateFormPartA from './FormParts/CSSZSickLeaveCertificateFormPartA';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import styles from './CSSZSickLeaveCertificateForm.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import {
	CSSZSickLeaveValues,
	CSSZSickLeaveSickLeaveSubmitPayload,
	CSSZSubmitValues,
} from './CSSZSickLeaveCertificateCreate';
import { DeepPartial } from '../../../../Interfaces/DeepPartial';
import {
	CSSZEmployment,
	CSSZSickLeaveCertificateFormValues,
	CSSZUser,
} from '../../Interfaces/CSSZ.interface';
import {
	buildEmploymentFromInsuranceAdministratorTermItem,
	hasInsuranceAdministratorAddressForEmployment,
	InsuranceAdministratorTermData,
	insuranceAdministratorNeedsManualProfession,
} from '../../Helpers/employmentFromInsuranceAdministrator.helper';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import {
	TermItem,
	useLazyTermItemByIdQuery,
	useMetaQuery,
} from '../../../../graphql/graphql';
import { useAppContext } from '../../../../Services/AppContext';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import {
	CSSZPermittedWalksValidationSchema,
	hasOutingsData,
} from '../../Validation/CSSZValidation';

interface Props {
	onSubmit: (values: CSSZSubmitValues, send?: boolean) => void;
	collectedValues?: DeepPartial<CSSZSickLeaveValues>;
	user: CSSZUser;
	showSubmitToCssz?: boolean;
	resetOnCollectedValuesChange?: boolean;
}

const validationScheme = yup.object({
	insuranceAdministrator: yup
		.object({
			value: yup
				.string()
				.required(
					'validation.CSSZSickLeaveCertificateForm.insuranceAdministrator',
				),
			label: yup.string(),
		})
		.required('validation.CSSZSickLeaveCertificateForm.insuranceAdministrator')
		.typeError(
			'validation.CSSZSickLeaveCertificateForm.insuranceAdministrator',
		),
	takeover: yup.boolean().nullable(),
	dateFrom: yup
		.string()
		.nullable()
		.required('validation.CSSZSickLeaveCertificateForm.incapacityStartDate'),
	diagnosis: yup
		.object({
			value: yup
				.string()
				.required('validation.CSSZSickLeaveCertificateForm.diagnosis'),
			label: yup.string(),
		})
		.required('validation.CSSZSickLeaveCertificateForm.diagnosis')
		.typeError('validation.CSSZSickLeaveCertificateForm.diagnosis'),
	issueDate: yup
		.string()
		.nullable()
		.required('validation.CSSZSickLeaveCertificateForm.issueDate'),
	injury: yup.boolean().nullable(),
	workAccident: yup.boolean().nullable(),
	injuryByOther: yup.boolean().nullable(),
	alcoholOrDrugs: yup.boolean().nullable(),
	outings: CSSZPermittedWalksValidationSchema.nullable(),
	profession: yup.string().when('professionRequired', {
		is: true,
		then: schema =>
			schema
				.trim()
				.required('validation.CSSZSickLeaveCertificateForm.profession'),
		otherwise: schema => schema.nullable(),
	}),
	professionRequired: yup.boolean().nullable(),
	submitToCssz: yup.boolean().optional(),
});

const getFormDefaultValues = (
	collectedValues?: DeepPartial<CSSZSickLeaveValues>,
	showSubmitToCssz = true,
) => {
	const sickLeave = collectedValues?.sickLeave ?? {};
	const { outings, ...sickLeaveRest } = sickLeave;

	return {
		...sickLeaveRest,
		...(hasOutingsData(outings) ? { outings } : {}),
		...(showSubmitToCssz ? { submitToCssz: true } : {}),
		profession: '',
		professionRequired: false,
		issueDate: collectedValues?.sickLeave?.createdAt
			? moment(collectedValues.sickLeave.createdAt).toISOString()
			: moment().toISOString(),
		dateFrom: collectedValues?.sickLeave?.dateFrom
			? moment(collectedValues.sickLeave.dateFrom).toISOString()
			: null,
	};
};

const CSSZSickLeaveCertificateForm: React.FC<Props> = ({
	onSubmit,
	collectedValues,
	user,
	showSubmitToCssz = true,
	resetOnCollectedValuesChange = true,
}) => {
	const { workplaceId } = useAppContext();
	const { data: sszMetaData } = useMetaQuery({
		workplaceId,
		key: 'csszSszCode',
	});
	const [getTermItemById] = useLazyTermItemByIdQuery();
	const [showProfessionField, setShowProfessionField] = useState(false);
	const employmentRef = useRef(collectedValues?.employment);

	employmentRef.current = collectedValues?.employment;

	const formMethods = useForm({
		defaultValues: getFormDefaultValues(collectedValues, showSubmitToCssz),
		resolver: yupResolver(validationScheme),
	});
	const { control, setValue, getValues } = formMethods;

	const professionWatch = useWatch({
		control,
		name: 'profession',
	});

	useEffect(() => {
		if (!resetOnCollectedValuesChange || !collectedValues?.sickLeave) {
			return;
		}

		formMethods.reset(getFormDefaultValues(collectedValues, showSubmitToCssz));
	}, [
		collectedValues,
		formMethods,
		resetOnCollectedValuesChange,
		showSubmitToCssz,
	]);

	useEffect(() => {
		if (!showProfessionField) {
			return;
		}

		const employment = employmentRef.current;
		if (!employment) {
			return;
		}

		const nextProfession = professionWatch ?? '';
		if ((employment.profession ?? '') === nextProfession) {
			return;
		}

		onSubmit(
			{
				type: 'employment',
				data: {
					...employment,
					profession: nextProfession,
				} as CSSZEmployment,
			},
			false,
		);
	}, [professionWatch, showProfessionField, onSubmit]);

	const handleSubmit = (values: CSSZSickLeaveSickLeaveSubmitPayload) => {
		onSubmit({ type: 'sickLeave', data: values }, true);
	};

	const onInsuranceAdministratorChange = useCallback(
		async (_value: SelectValue, termItem?: TermItem) => {
			const clearEmploymentFromAdmin = () => {
				setShowProfessionField(false);
				setValue('profession', '', { shouldDirty: true });
				setValue('professionRequired', false);
				onSubmit({ type: 'employment', data: undefined }, false);
			};

			if (!termItem) {
				clearEmploymentFromAdmin();
				return;
			}

			const raw = termItem.data as InsuranceAdministratorTermData | null;

			if (!hasInsuranceAdministratorAddressForEmployment(raw)) {
				clearEmploymentFromAdmin();
				return;
			}

			const needsManualProfession =
				insuranceAdministratorNeedsManualProfession(raw);

			setShowProfessionField(needsManualProfession);
			setValue('professionRequired', needsManualProfession);
			setValue('profession', '', { shouldDirty: true });

			const { data } = await getTermItemById({
				id: raw!.address!.countryTermId,
			});

			if (!data?.termItem) {
				clearEmploymentFromAdmin();
				return;
			}

			const employment = buildEmploymentFromInsuranceAdministratorTermItem(
				termItem,
				raw!,
				data.termItem,
			);
			if (needsManualProfession) {
				employment.profession = getValues().profession ?? '';
			}

			onSubmit({ type: 'employment', data: employment }, false);
		},
		[getTermItemById, onSubmit, setValue, getValues],
	);

	const formPartCProps: CSSZSickLeaveCertificateFormPartCProps = {
		onSubmit,
		collectedValues,
		patientId: user.id,
		showProfessionInput: showProfessionField,
		onInsuranceAdministratorChange,
	};

	return (
		<FormWrapper formMethods={formMethods} className={styles.wrapper}>
			<div className='row'>
				<div className='col-6'>
					{collectedValues?.decisionNumber && (
						<div className={styles.item}>
							<Text
								tx='component.CSSZSickLeaveCertificateForm.DPNDecisionNumber'
								inline
								colon
							/>{' '}
							<b>{collectedValues?.decisionNumber}</b>
						</div>
					)}
					<div className={styles.item}>
						<Text tx='OSSZ' inline colon />{' '}
						{sszMetaData?.meta?.value?.label || ''}
					</div>
				</div>
			</div>
			<CSSZSickLeaveCertificateFormPartA user={user} />
			<CSSZSickLeaveCertificateFormPartB
				patientId={user.id}
				onSubmit={onSubmit}
				collectedValues={collectedValues}
			/>
			<CSSZSickLeaveCertificateFormPartC {...formPartCProps} />
			<CSSZSickLeaveCertificateFormPartD />
			<CSSZSickLeaveCertificateFormPartE />
			<div className={styles.submitRow}>
				<SubmitButton onSubmit={handleSubmit} title={'base.shared.save'} />
				{showSubmitToCssz && (
					<CheckboxField
						name='submitToCssz'
						tx='component.CSSZSickLeaveCertificateCreate.submitToCsszLabel'
						className={styles.submitToCssz}
					/>
				)}
			</div>
		</FormWrapper>
	);
};

export default CSSZSickLeaveCertificateForm;
